import { useEffect, useReducer, useRef, useState } from "react";
import type { ReactNode } from "react";
import type {
  AssetCostOverride,
  BatteryAsset,
  ContentEvent,
  CostBreakdown,
  EnergyAsset,
  HouseholdBuildingType,
  HouseholdSize,
  PlanDraftV2,
  PlanEvaluationV2,
  Scenario,
  SolarAsset,
  SolarVariant,
} from "@energieplaner/contracts";
import {
  DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH,
  CURRENT_PLANNING_DEFAULTS,
  HOUSEHOLD_CONSUMPTION_PRESETS_BY_BUILDING,
  HOUSEHOLD_H0_LOAD_PROFILE,
  LegacyPlanDraftV1Schema,
  PlanDraftSchema,
} from "@energieplaner/contracts";

const STORAGE_KEY = "energieplaner:draft:v2";
const LEGACY_STORAGE_KEY = "energieplaner:draft:v1";
const SOLAR_COSTS_ARTICLE_PATH = "/wissen/was-kostet-eine-solaranlage/";
const BATTERY_COSTS_ARTICLE_PATH = "/wissen/was-kostet-ein-stromspeicher/";
const HOME_CONTEXT_STORAGE_KEY = "energieplaner:home-context:v1";
const CONTEXT_DRAFTS_STORAGE_KEY = "energieplaner:drafts-by-home-context:v1";

const HOME_CONTEXTS = [
  {
    id: "house",
    label: "Haus",
    consumptionType: "house",
    src: "/images/energiehaus-solarpunk-pixel-v1.png",
    alt: "Pixel-Art-Darstellung eines Hauses mit Solardach, Batteriespeicher und Garten",
  },
  {
    id: "balcony",
    label: "Wohnung",
    consumptionType: "apartment",
    src: "/images/energiehaus-balkon-pixel-v1.png",
    alt: "Pixel-Art-Darstellung eines Balkons mit zwei Solarmodulen, Pflanzen und kleinem Wechselrichter",
  },
] as const;

type HomeContext = (typeof HOME_CONTEXTS)[number]["id"];
type ContextDrafts = Partial<Record<HomeContext, PlanDraftV2>>;

function homeContextConfig(context: HomeContext) {
  return (
    HOME_CONTEXTS.find((option) => option.id === context) ?? HOME_CONTEXTS[0]
  );
}

function consumptionPresetsForContext(context: HomeContext) {
  const buildingType: HouseholdBuildingType =
    homeContextConfig(context).consumptionType;
  return HOUSEHOLD_CONSUMPTION_PRESETS_BY_BUILDING[buildingType];
}

const INITIAL_DRAFT: PlanDraftV2 = {
  commissioningDate: "2026-08-16",
  location: { postalCode: "10115" },
  household: {
    householdSize: 2,
    annualElectricityKwh: 2700,
    electricityPriceEurPerKwh: DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH,
  },
  roof: { tiltDeg: 35, azimuthDeg: 0, shading: "low" },
  assets: [],
};

type Acquisition = "buy" | "owned";

type PlannerState = {
  draft: PlanDraftV2;
  evaluation: PlanEvaluationV2 | null;
  status: "idle" | "updating" | "ready" | "error";
  error: string | null;
  assumptionsOpen: boolean;
  stashed: {
    solar?: Partial<Record<SolarVariant, SolarAsset>>;
    battery?: BatteryAsset;
  };
};

type AssetKind = "solar" | "battery";

type Action =
  | { type: "draft"; draft: PlanDraftV2 }
  | { type: "context"; draft: PlanDraftV2 }
  | { type: "result"; evaluation: PlanEvaluationV2 }
  | { type: "error"; message: string }
  | { type: "status"; status: PlannerState["status"] }
  | { type: "assumptions" }
  | {
      type: "toggle";
      component: AssetKind;
      solarVariant?: SolarVariant;
    };

const initialState: PlannerState = {
  draft: INITIAL_DRAFT,
  evaluation: null,
  status: "idle",
  error: null,
  assumptionsOpen: false,
  stashed: {},
};

function reducer(state: PlannerState, action: Action): PlannerState {
  if (action.type === "draft")
    return { ...state, draft: action.draft, status: "updating", error: null };
  if (action.type === "context")
    return {
      ...state,
      draft: action.draft,
      evaluation: null,
      status: "updating",
      error: null,
      stashed: {},
    };
  if (action.type === "result")
    return {
      ...state,
      evaluation: action.evaluation,
      status: "ready",
      error: null,
    };
  if (action.type === "error")
    return { ...state, status: "error", error: action.message };
  if (action.type === "status") return { ...state, status: action.status };
  if (action.type === "assumptions")
    return { ...state, assumptionsOpen: !state.assumptionsOpen };
  const key = action.component;
  const solarVariant =
    key === "solar" ? action.solarVariant ?? "rooftop" : undefined;
  const existing =
    key === "solar" && solarVariant
      ? findSolarAssetByVariant(state.draft.assets, solarVariant)
      : findAssetByKind(state.draft.assets, key);
  if (existing) {
    return {
      ...state,
      draft: {
        ...state.draft,
        assets: state.draft.assets.filter((asset) => asset.id !== existing.id),
      },
      stashed:
        key === "solar" && solarVariant
          ? {
              ...state.stashed,
              solar: { ...state.stashed.solar, [solarVariant]: existing },
            }
          : { ...state.stashed, [key]: existing },
      status: "updating",
    };
  }
  const restored: EnergyAsset | undefined =
    key === "solar" && solarVariant
      ? state.stashed.solar?.[solarVariant]
      : state.stashed.battery;
  const defaults =
    key === "solar"
      ? {
          id: solarVariant === "balcony" ? "balcony-pv-1" : "rooftop-pv-1",
          kind: "solar" as const,
          variant: solarVariant ?? "rooftop",
          state: "new" as const,
          capacityKwp:
            solarVariant === "balcony"
              ? CURRENT_PLANNING_DEFAULTS.balconyPvCapacityKwp
              : (state.evaluation?.suggestions.pv.recommendedKwp ?? 4),
          ...(solarVariant === "balcony"
            ? { inverterCapacityKva: 0.8 }
            : {}),
        }
      : {
          id: "home-battery-1",
          kind: "battery" as const,
          state: "new" as const,
          usableCapacityKwh:
            state.evaluation?.suggestions.battery.recommendedKwh ?? 4,
          replacement: { year: 13, costFraction: 0.5 },
        };
  return {
    ...state,
    draft: {
      ...state.draft,
      assets: [...state.draft.assets, restored ?? defaults],
    },
    status: "updating",
  };
}

export function App() {
  const [state, dispatch] = useReducer(reducer, initialState, hydrateState);
  const [homeContext, setHomeContext] =
    useState<HomeContext>(hydrateHomeContext);
  const [reducedMotion, setReducedMotion] = useState(false);
  const attributionRef = useRef<ContentAttribution | null>(null);
  const initialDraftRef = useRef(JSON.stringify(state.draft));
  const plannerStartedRef = useRef(false);

  useEffect(() => {
    attributionRef.current = hydrateContentAttribution();
  }, []);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(media.matches);
    const listener = () => setReducedMotion(media.matches);
    media.addEventListener?.("change", listener);
    return () => media.removeEventListener?.("change", listener);
  }, []);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.draft));
    persistContextDraft(homeContext, state.draft);
  }, [homeContext, state.draft]);

  useEffect(() => {
    window.localStorage.setItem(HOME_CONTEXT_STORAGE_KEY, homeContext);
  }, [homeContext]);

  useEffect(() => {
    if (
      !plannerStartedRef.current &&
      JSON.stringify(state.draft) !== initialDraftRef.current
    ) {
      plannerStartedRef.current = true;
      sendContentEvent("planner_start", attributionRef.current);
    }
  }, [state.draft]);

  useEffect(() => {
    const controller = new AbortController();
    const timeout = window.setTimeout(async () => {
      dispatch({ type: "status", status: "updating" });
      try {
        const response = await fetch("/v1/plan-evaluations", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(state.draft),
          signal: controller.signal,
        });
        const body = (await response.json()) as
          PlanEvaluationV2 | { message?: string };
        if (!response.ok)
          throw new Error(
            "message" in body && body.message
              ? body.message
              : "Der Plan konnte nicht berechnet werden.",
          );
        sendContentEvent("evaluation_complete", attributionRef.current);
        dispatch({ type: "result", evaluation: body as PlanEvaluationV2 });
      } catch (error) {
        if (!controller.signal.aborted)
          dispatch({
            type: "error",
            message:
              error instanceof Error
                ? error.message
                : "Der Plan konnte nicht berechnet werden.",
          });
      }
    }, 350);
    return () => {
      controller.abort();
      window.clearTimeout(timeout);
    };
  }, [state.draft]);

  const patchDraft = (patch: (draft: PlanDraftV2) => PlanDraftV2) =>
    dispatch({ type: "draft", draft: patch(state.draft) });
  const toggleSolar = (variant: SolarVariant) =>
    dispatch({ type: "toggle", component: "solar", solarVariant: variant });
  const changeHomeContext = (context: HomeContext) => {
    if (context === homeContext) return;
    persistContextDraft(homeContext, state.draft);
    const storedDraft = readContextDrafts()[context];
    const nextDraft =
      storedDraft ?? createDraftForContext(state.draft, context);
    setHomeContext(context);
    dispatch({ type: "context", draft: nextDraft });
  };
  const chooseHouseholdSize = (size: HouseholdSize) => {
    const preset = consumptionPresetsForContext(homeContext).find(
      (candidate) => candidate.size === size,
    );
    if (!preset) return;
    patchDraft((draft) => ({
      ...draft,
      household: {
        ...draft.household,
        householdSize: preset.size,
        annualElectricityKwh: preset.annualElectricityKwh,
      },
    }));
  };
  const selected = state.evaluation?.selected;
  const contextProfile = homeContextConfig(homeContext);
  const consumptionPresets = consumptionPresetsForContext(homeContext);
  const solarAssets = state.draft.assets.filter(
    (asset): asset is SolarAsset => asset.kind === "solar",
  );
  const rooftopSolar = solarAssets.find((asset) => asset.variant === "rooftop");
  const balconySolar = solarAssets.find((asset) => asset.variant === "balcony");
  const battery = findAssetByKind(state.draft.assets, "battery");
  const hasBattery = Boolean(battery);

  return (
    <>
      <main className={reducedMotion ? "reduced-motion" : ""}>
        <header className="site-header content-width">
          <a
            className="wordmark"
            href="/"
            aria-label="Energieplaner Startseite"
          >
            <span className="wordmark-mark">✳</span> energieplaner
          </a>
          <nav className="site-nav" aria-label="Hauptnavigation">
            <a className="site-nav-link is-active" href="/" aria-current="page">
              Planer
            </a>
            <a className="site-nav-link" href="/wissen/">
              Wissen
            </a>
            <a className="site-nav-link" href="/methodik/">
              Methodik
            </a>
          </nav>
        </header>

        <section className="intro content-width">
          <h1>
            Plane dein <em>Energiesystem.</em>
          </h1>
          <p className="intro-copy">
            Solar, Speicher und Kosten — verständlich gerechnet.
          </p>
        </section>

        <div className="planner-layout content-width">
          <section
            className="section-block first-section"
            aria-labelledby="home-heading"
          >
            <SectionHeading
              number="01"
              title="Plan einrichten"
              id="home-heading"
            />
            <div className="first-section-layout">
              <aside
                className="home-column"
                aria-label="Darstellung des Zuhauses"
              >
                <div className="home-card">
                  <div className="home-card-label">dein Zuhause</div>
                  <HouseIllustration
                    context={homeContext}
                    onContextChange={changeHomeContext}
                  />
                </div>
                <ImpactRibbon selected={selected} />
                <CostEstimate
                  draft={state.draft}
                  evaluation={state.evaluation}
                />
                <p className="privacy-note">
                  Dein Entwurf bleibt in diesem Browser. Für die Berechnung wird
                  er an Energieplaner übertragen, aber nicht als Projekt
                  gespeichert.
                </p>
              </aside>

              <div className="configuration-column">
                <p className="section-lede setup-intro">
                  Beschreibe zuerst dein Zuhause und stelle danach die Systeme
                  zusammen, die du kaufen möchtest oder bereits hast.
                </p>
                <div className="field-grid two-up">
                  <Field
                    label="Deutsche Postleitzahl"
                    hint="Wird für die regionale Solarertragsabfrage verwendet."
                  >
                    <input
                      aria-label="Deutsche Postleitzahl"
                      inputMode="numeric"
                      maxLength={5}
                      value={state.draft.location.postalCode}
                      onChange={(event) =>
                        patchDraft((draft) => ({
                          ...draft,
                          location: {
                            ...draft.location,
                            postalCode: event.target.value
                              .replace(/\D/g, "")
                              .slice(0, 5),
                          },
                        }))
                      }
                    />
                  </Field>
                  <Field
                    label="Strompreis"
                    hint={`Nutze den Arbeitspreis aus deiner letzten Rechnung. Bei diesem Verbrauch sind das ungefähr ${euro((state.draft.household.electricityPriceEurPerKwh ?? DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH) * state.draft.household.annualElectricityKwh)} pro Jahr.`}
                  >
                    <div className="input-with-unit">
                      <input
                        aria-label="Strompreis"
                        type="number"
                        min="0"
                        max="10"
                        step="0.01"
                        value={
                          state.draft.household.electricityPriceEurPerKwh ??
                          DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH
                        }
                        onChange={(event) =>
                          patchDraft((draft) => ({
                            ...draft,
                            household: {
                              ...draft.household,
                              electricityPriceEurPerKwh: numeric(
                                event.target.value,
                                DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH,
                              ),
                            },
                          }))
                        }
                      />
                      <span>€ / kWh</span>
                    </div>
                  </Field>
                </div>
                <div className="field-grid two-up consumption-inputs">
                  <Field
                    label="Jährlicher Stromverbrauch"
                    hint="Nutze deine letzte Jahresabrechnung oder wähle einen Ausgangspunkt."
                  >
                    <div className="input-with-unit">
                      <input
                        aria-label="Jährlicher Stromverbrauch"
                        type="number"
                        min="500"
                        max="100000"
                        step="100"
                        value={state.draft.household.annualElectricityKwh}
                        onChange={(event) =>
                          patchDraft((draft) => ({
                            ...draft,
                            household: {
                              ...draft.household,
                              householdSize: undefined,
                              annualElectricityKwh: numeric(
                                event.target.value,
                                draft.household.annualElectricityKwh,
                              ),
                            },
                          }))
                        }
                      />
                      <span>kWh / Jahr</span>
                    </div>
                  </Field>
                  <div className="field">
                    <span className="field-label">Schnellschätzung</span>
                    <div
                      className="household-preset-buttons"
                      role="group"
                      aria-label="Voreinstellungen für die Haushaltsgröße"
                    >
                      {consumptionPresets.map((preset) => (
                        <button
                          className={
                            state.draft.household.householdSize === preset.size
                              ? "household-preset active"
                              : "household-preset"
                          }
                          key={preset.size}
                          type="button"
                          onClick={() => chooseHouseholdSize(preset.size)}
                        >
                          {preset.label}
                          <strong>
                            {preset.annualElectricityKwh.toLocaleString(
                              "de-DE",
                            )}{" "}
                            kWh
                          </strong>
                        </button>
                      ))}
                    </div>
                    <small>
                      Stromspiegel-Ausgangspunkt für{" "}
                      {contextProfile.consumptionType === "house"
                        ? "ein Einfamilienhaus"
                        : "eine Wohnung"}{" "}
                      ohne elektrische Warmwasserbereitung.
                    </small>
                  </div>
                </div>
                <ConsumptionProfileNote />
                <div className="roof-row">
                  <div>
                    <label htmlFor="tilt">Dachneigung</label>
                    <output>{state.draft.roof.tiltDeg}°</output>
                  </div>
                  <input
                    id="tilt"
                    type="range"
                    min="0"
                    max="60"
                    step="1"
                    value={state.draft.roof.tiltDeg}
                    onChange={(event) =>
                      patchDraft((draft) => ({
                        ...draft,
                        roof: {
                          ...draft.roof,
                          tiltDeg: Number(event.target.value),
                        },
                      }))
                    }
                  />
                  <label className="compact-select">
                    Verschattung{" "}
                    <select
                      aria-label="Verschattung des Dachs"
                      value={state.draft.roof.shading}
                      onChange={(event) =>
                        patchDraft((draft) => ({
                          ...draft,
                          roof: {
                            ...draft.roof,
                            shading: event.target
                              .value as PlanDraftV2["roof"]["shading"],
                          },
                        }))
                      }
                    >
                      <option value="none">keine</option>
                      <option value="low">gering</option>
                      <option value="medium">mittel</option>
                      <option value="high">hoch</option>
                    </select>
                  </label>
                </div>
                <div className="setup-divider" />
                <div
                  className="setup-subsection"
                  aria-labelledby="system-heading"
                >
                  <div className="setup-subheading">
                    <span className="panel-tag">Komponenten</span>
                    <h3 id="system-heading">Energiesystem zusammenstellen</h3>
                  </div>
                  <p className="section-lede">
                    Wähle ein System, um es zum Zuhause hinzuzufügen. Jede
                    vorgeschlagene Größe lässt sich ändern und ist als
                    Ausgangspunkt für die Planung gekennzeichnet, nicht als
                    Empfehlung für die technische Auslegung.
                  </p>
                  <div
                    className="component-picker"
                    role="group"
                    aria-label="Energiekomponenten"
                  >
                    {homeContext === "house" && (
                      <ComponentButton
                        icon="☼"
                        title="Solaranlage auf dem Dach"
                        subtitle={
                          rooftopSolar
                            ? `${rooftopSolar.capacityKwp} kWp ausgewählt`
                            : "eigenen Strom erzeugen"
                        }
                        active={Boolean(rooftopSolar)}
                        onClick={() => toggleSolar("rooftop")}
                      />
                    )}
                    <ComponentButton
                      icon="▥"
                      title="Balkon-Solaranlage"
                      subtitle={
                        balconySolar
                          ? `${balconySolar.capacityKwp} kWp ausgewählt`
                          : "zusätzlichen Solarstrom erzeugen"
                      }
                      active={Boolean(balconySolar)}
                      onClick={() => toggleSolar("balcony")}
                    />
                    <ComponentButton
                      icon="▣"
                      title="Batteriespeicher"
                      subtitle={
                        hasBattery
                          ? `${battery?.usableCapacityKwh} kWh nutzbar`
                          : "mehr Solarstrom nutzen"
                      }
                      active={hasBattery}
                      onClick={() =>
                        dispatch({ type: "toggle", component: "battery" })
                      }
                    />
                  </div>
                  <div className="component-stack">
                    {solarAssets.map((solarAsset) => (
                      <SolarControl
                        key={solarAsset.id}
                        solar={solarAsset}
                        evaluation={state.evaluation}
                        patchDraft={patchDraft}
                        onRemove={() =>
                          dispatch({
                            type: "toggle",
                            component: "solar",
                            solarVariant: solarAsset.variant,
                          })
                        }
                      />
                    ))}
                    {hasBattery && (
                      <BatteryControl
                        draft={state.draft}
                        evaluation={state.evaluation}
                        patchDraft={patchDraft}
                        onRemove={() =>
                          dispatch({ type: "toggle", component: "battery" })
                        }
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section
            className="section-block results-section"
            aria-labelledby="results-heading"
          >
            <SectionHeading
              number="02"
              title="Wann trägt sich dein Plan?"
              id="results-heading"
            />
            <ResultNarrative
              evaluation={state.evaluation}
              error={state.error}
            />
            <PaybackChart evaluation={state.evaluation} />
            <Warnings evaluation={state.evaluation} />
          </section>

          <section
            className="section-block assumptions-section"
            aria-labelledby="assumptions-heading"
          >
            <button
              className="assumptions-toggle"
              type="button"
              onClick={() => dispatch({ type: "assumptions" })}
              aria-expanded={state.assumptionsOpen}
              aria-controls="assumptions-detail"
            >
              <span>
                <span className="section-number">03</span> Annahmen und Methode
              </span>
              <span className="toggle-symbol">
                {state.assumptionsOpen ? "−" : "+"}
              </span>
            </button>
            {state.assumptionsOpen && (
              <AssumptionsDetail evaluation={state.evaluation} />
            )}
          </section>
        </div>
      </main>
      <footer className="site-footer content-width">
        <span>Transparente Energieplanung für deutsche Wohnhäuser.</span>
        <nav aria-label="Weitere Links">
          <a href="/datenschutz/">Datenschutz</a>
          <a href="/impressum/">Impressum</a>
        </nav>
      </footer>
    </>
  );
}

type ContentAttribution = Pick<
  ContentEvent,
  "contentId" | "utmSource" | "utmMedium" | "utmCampaign" | "referrerCategory"
>;

function hydrateContentAttribution(): ContentAttribution | null {
  if (typeof window === "undefined") return null;
  const query = new URLSearchParams(window.location.search);
  const fromQuery = sanitizeAttribution({
    contentId: query.get("ep_content") ?? undefined,
    utmSource: query.get("utm_source") ?? undefined,
    utmMedium: query.get("utm_medium") ?? undefined,
    utmCampaign: query.get("utm_campaign") ?? undefined,
    referrerCategory: query.get("referrer_category") as
      ContentEvent["referrerCategory"] | null,
  });
  const attribution = fromQuery?.contentId ? fromQuery : null;
  if (window.location.search) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.hash}`,
    );
  }
  return attribution;
}

function sanitizeAttribution(value: unknown): ContentAttribution | null {
  if (!value || typeof value !== "object") return null;
  const input = value as Record<string, unknown>;
  const slug =
    typeof input.contentId === "string" ? input.contentId : undefined;
  const token = (name: string, max: number) => {
    const value = input[name];
    return typeof value === "string" &&
      new RegExp(`^[a-z0-9_-]{1,${max}}$`).test(value)
      ? value
      : undefined;
  };
  const contentId =
    slug && /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug) ? slug : undefined;
  const category = input.referrerCategory;
  const referrerCategory =
    category === "organic" ||
    category === "social" ||
    category === "partner" ||
    category === "direct" ||
    category === "other"
      ? category
      : undefined;
  if (!contentId && !token("utmSource", 64) && !token("utmCampaign", 96))
    return null;
  return {
    contentId,
    utmSource: token("utmSource", 64),
    utmMedium: token("utmMedium", 64),
    utmCampaign: token("utmCampaign", 96),
    referrerCategory,
  };
}

function sendContentEvent(
  eventName: ContentEvent["eventName"],
  attribution: ContentAttribution | null,
) {
  if (typeof window === "undefined") return;
  const payload: ContentEvent = {
    eventName,
    contentId: attribution?.contentId,
    referrerCategory: attribution?.referrerCategory ?? "direct",
    utmSource: attribution?.utmSource,
    utmMedium: attribution?.utmMedium,
    utmCampaign: attribution?.utmCampaign,
    deviceClass:
      window.innerWidth < 640
        ? "mobile"
        : window.innerWidth < 1024
          ? "tablet"
          : "desktop",
  };
  const body = JSON.stringify(payload);
  if (navigator.sendBeacon) {
    navigator.sendBeacon(
      "/v1/events",
      new Blob([body], { type: "application/json" }),
    );
    return;
  }
  void fetch("/v1/events", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  });
}

function readContextDrafts(): ContextDrafts {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(CONTEXT_DRAFTS_STORAGE_KEY);
    if (!raw) return {};
    const stored = JSON.parse(raw) as Record<string, unknown>;
    return HOME_CONTEXTS.reduce<ContextDrafts>((drafts, option) => {
      const parsed = PlanDraftSchema.safeParse(stored[option.id]);
      if (parsed.success) drafts[option.id] = parsed.data;
      return drafts;
    }, {});
  } catch {
    return {};
  }
}

function persistContextDraft(context: HomeContext, draft: PlanDraftV2) {
  if (typeof window === "undefined") return;
  const drafts = readContextDrafts();
  drafts[context] = draft;
  window.localStorage.setItem(
    CONTEXT_DRAFTS_STORAGE_KEY,
    JSON.stringify(drafts),
  );
}

function createDraftForContext(
  source: PlanDraftV2,
  context: HomeContext,
): PlanDraftV2 {
  const householdSize = source.household.householdSize;
  const preset = householdSize
    ? consumptionPresetsForContext(context).find(
        (candidate) => candidate.size === householdSize,
      )
    : undefined;
  return {
    ...source,
    household: {
      ...source.household,
      annualElectricityKwh:
        preset?.annualElectricityKwh ?? source.household.annualElectricityKwh,
    },
    assets: [],
  };
}

function sanitizeDraftForContext(
  source: PlanDraftV2,
  context: HomeContext,
): PlanDraftV2 {
  const allowedSolarVariants: SolarVariant[] =
    context === "house" ? ["rooftop", "balcony"] : ["balcony"];
  return {
    ...source,
    assets: source.assets.filter(
      (asset) =>
        asset.kind !== "solar" || allowedSolarVariants.includes(asset.variant),
    ),
  };
}

function hydrateState(): PlannerState {
  if (typeof window === "undefined") return initialState;
  try {
    const context = hydrateHomeContext();
    const storedContextDraft = readContextDrafts()[context];
    if (storedContextDraft) {
      return { ...initialState, draft: storedContextDraft };
    }

    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? PlanDraftSchema.safeParse(JSON.parse(raw)) : null;
    if (parsed?.success) {
      return {
        ...initialState,
        draft: sanitizeDraftForContext(parsed.data, context),
      };
    }

    const legacyRaw = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    const legacy = legacyRaw
      ? LegacyPlanDraftV1Schema.safeParse(JSON.parse(legacyRaw))
      : null;
    if (legacy?.success) {
      const migrated = sanitizeDraftForContext(
        migrateLegacyDraft(legacy.data),
        context,
      );
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      return { ...initialState, draft: migrated };
    }
    return initialState;
  } catch {
    return initialState;
  }
}

function hydrateHomeContext(): HomeContext {
  if (typeof window === "undefined") return "house";
  const stored = window.localStorage.getItem(HOME_CONTEXT_STORAGE_KEY);
  return HOME_CONTEXTS.some((option) => option.id === stored)
    ? (stored as HomeContext)
    : "house";
}

function migrateLegacyDraft(legacy: {
  commissioningDate: string;
  location: PlanDraftV2["location"];
  household: PlanDraftV2["household"];
  roof: PlanDraftV2["roof"];
  components: {
    solar?: {
      capacityKwp: number;
      acquisition?: Acquisition;
      installedCostEur?: number;
    };
    battery?: {
      usableCapacityKwh: number;
      acquisition?: Acquisition;
      installedCostEur?: number;
      replacementYear?: number;
      replacementCostFraction?: number;
    };
  };
  assumptionOverrides?: PlanDraftV2["assumptionOverrides"];
}): PlanDraftV2 {
  const assets: EnergyAsset[] = [];
  if (legacy.components.solar) {
    const solar = legacy.components.solar;
    assets.push({
      id: "rooftop-pv-1",
      kind: "solar",
      variant: "rooftop",
      state: solar.acquisition === "owned" ? "existing" : "new",
      capacityKwp: solar.capacityKwp,
      ...(solar.installedCostEur !== undefined
        ? { costOverride: { totalEur: solar.installedCostEur } }
        : {}),
    });
  }
  if (legacy.components.battery) {
    const battery = legacy.components.battery;
    assets.push({
      id: "home-battery-1",
      kind: "battery",
      state: battery.acquisition === "owned" ? "existing" : "new",
      usableCapacityKwh: battery.usableCapacityKwh,
      ...(battery.installedCostEur !== undefined
        ? { costOverride: { totalEur: battery.installedCostEur } }
        : {}),
      replacement: {
        year: battery.replacementYear ?? 13,
        costFraction: battery.replacementCostFraction ?? 0.5,
      },
    });
  }
  return {
    commissioningDate: legacy.commissioningDate,
    location: legacy.location,
    household: legacy.household,
    roof: legacy.roof,
    assets,
    assumptionOverrides: legacy.assumptionOverrides,
  };
}

function findAssetByKind<K extends AssetKind>(
  assets: EnergyAsset[],
  kind: K,
): Extract<EnergyAsset, { kind: K }> | undefined {
  return assets.find((asset) => asset.kind === kind) as
    Extract<EnergyAsset, { kind: K }> | undefined;
}

function findSolarAssetByVariant(
  assets: EnergyAsset[],
  variant: SolarVariant,
): SolarAsset | undefined {
  return assets.find(
    (asset): asset is SolarAsset =>
      asset.kind === "solar" && asset.variant === variant,
  );
}

function ConsumptionProfileNote() {
  const max = Math.max(...HOUSEHOLD_H0_LOAD_PROFILE);
  return (
    <div className="profile-note">
      <div className="profile-note-heading">
        <span className="field-label">Typischer Tagesverlauf</span>
        <span>mehr Verbrauch am Morgen und Abend</span>
      </div>
      <div
        className="profile-bars"
        role="img"
        aria-label="Typischer Stromverbrauch eines Haushalts mit einem Morgen- und einem stärkeren Abendmaximum"
      >
        {HOUSEHOLD_H0_LOAD_PROFILE.map((value, hour) => (
          <span
            key={hour}
            style={{ height: `${Math.max(8, (value / max) * 100)}%` }}
            title={`${String(hour).padStart(2, "0")}:00`}
          />
        ))}
      </div>
      <div className="profile-note-footer">
        <span>00:00</span>
        <span>12:00</span>
        <span>24:00</span>
      </div>
    </div>
  );
}

function HouseIllustration({
  context,
  onContextChange,
}: {
  context: HomeContext;
  onContextChange: (context: HomeContext) => void;
}) {
  const selected =
    HOME_CONTEXTS.find((option) => option.id === context) ?? HOME_CONTEXTS[0];

  return (
    <div className="house-illustration">
      <div
        className="home-context-switcher"
        role="group"
        aria-label="Art des Zuhauses"
      >
        {HOME_CONTEXTS.map((option) => (
          <button
            className={
              option.id === context
                ? "home-context-option active"
                : "home-context-option"
            }
            key={option.id}
            type="button"
            aria-pressed={option.id === context}
            onClick={() => onContextChange(option.id)}
          >
            <span>{option.label}</span>
          </button>
        ))}
      </div>
      <div className="house-image-frame">
        <img
          key={selected.id}
          src={selected.src}
          alt={selected.alt}
          width="1448"
          height="1086"
        />
      </div>
    </div>
  );
}

function ImpactRibbon({ selected }: { selected?: Scenario }) {
  return (
    <div className="impact-ribbon" aria-label="Aktuelle Auswirkungen des Plans">
      <div>
        <span className="ribbon-label">Anfangsinvestition</span>
        <strong>
          {selected ? euro(selected.metrics.upfrontCostEur) : "…"}
        </strong>
      </div>
      <div className="ribbon-divider" />
      <div>
        <span className="ribbon-label">Nachhaltige Amortisation</span>
        <strong>{selected ? payback(selected) : "…"}</strong>
      </div>
      <div className="ribbon-confidence">
        {selected?.unavailableReason
          ? "eine weitere Eingabe nötig"
          : "Euro von heute · Kosten über den Lebenszyklus enthalten"}
      </div>
    </div>
  );
}

function ResultNarrative({
  evaluation,
  error,
}: {
  evaluation: PlanEvaluationV2 | null;
  error: string | null;
}) {
  if (error)
    return (
      <div className="error-panel" role="alert">
        <strong>Der Rechendienst konnte nicht erreicht werden.</strong>
        <span>{error} Starte die lokale Vorschau und versuche es erneut.</span>
      </div>
    );
  if (!evaluation)
    return (
      <div className="loading-panel">
        <span className="loader-dot" /> Ausgangslage und standortbezogene
        Vorschläge werden berechnet …
      </div>
    );
  const selected = evaluation.selected;
  const firstYearFlow = selected.annualFlows[0];
  const battery = evaluation.verdicts.find(
    (item) => item.component === "battery",
  );
  return (
    <div className="narrative" aria-live="polite">
      <p className="narrative-lede">
        {selected.metrics.npvEur === null ? (
          "Dein Energieeffekt ist bereit; für die Amortisation fehlt noch ein Kostenwert."
        ) : (
          <>
            Dieser Plan kostet etwa{" "}
            <strong>{euro(selected.metrics.upfrontCostEur)}</strong> und
            amortisiert sich im Modell{" "}
            {selected.metrics.sustainedPaybackYears === null ? (
              "nicht innerhalb des Analysezeitraums"
            ) : (
              <>
                nachhaltig nach{" "}
                <strong>{selected.metrics.sustainedPaybackYears} Jahren</strong>
              </>
            )}
            .
          </>
        )}
      </p>
      <div className="narrative-details">
        <span>
          {selected.metrics.selfConsumptionPct}% Eigenverbrauch{" "}
          <InfoTip
            id="eigenverbrauch-info"
            label="Eigenverbrauch"
            text="Anteil des erzeugten Solarstroms, den du selbst nutzt oder in den Speicher lädst, statt ihn einzuspeisen."
          />
        </span>
        <span>
          {selected.metrics.selfSufficiencyPct}% Autarkie{" "}
          <InfoTip
            id="autarkie-info"
            label="Autarkie"
            text="Anteil deines gesamten Stromverbrauchs, den Solarstrom direkt oder über den Speicher deckt. Der Rest kommt aus dem Netz."
          />
        </span>
        {firstYearFlow && firstYearFlow.generationKwh > 0 && (
          <span>
            Verpasster Eigenverbrauchswert:{" "}
            {euro(firstYearFlow.missedSelfConsumptionValueEur)} / Jahr{" "}
            <InfoTip
              id="missed-self-consumption-info"
              label="verpasster Eigenverbrauchswert"
              text="Geschätzter Wert des Solarstroms, den du einspeist, obwohl er theoretisch Netzstrom hätte ersetzen können. Kein zusätzlicher Kostenposten."
            />
          </span>
        )}
        {battery && (
          <span className={battery.verdict === "hurts" ? "negative" : ""}>
            Speicher:{" "}
            {battery.verdict === "unavailable"
              ? "Eingaben fehlen"
              : verdictLabel(battery.verdict)}
          </span>
        )}
      </div>
    </div>
  );
}

function InfoTip({
  id,
  label,
  text,
}: {
  id: string;
  label: string;
  text: string;
}) {
  return (
    <span className="metric-info">
      <button
        className="metric-info-trigger"
        type="button"
        aria-label={`Erklärung zu ${label}: ${text}`}
        aria-describedby={id}
      >
        <span aria-hidden="true">i</span>
      </button>
      <span className="metric-info-tooltip" id={id} role="tooltip">
        {text}
      </span>
    </span>
  );
}

function PaybackChart({ evaluation }: { evaluation: PlanEvaluationV2 | null }) {
  if (!evaluation) return null;

  const scenario = evaluation.selected;
  const positions = [-scenario.metrics.upfrontCostEur];
  for (const flow of scenario.annualFlows) {
    const previous = positions[positions.length - 1];
    positions.push(
      flow.cashBenefitEur === null ? previous : previous + flow.cashBenefitEur,
    );
  }

  const width = 760;
  const height = 320;
  const padding = { top: 28, right: 22, bottom: 42, left: 58 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  const minValue = Math.min(0, ...positions);
  const maxValue = Math.max(0, ...positions);
  const range = Math.max(1, maxValue - minValue);
  const barSlotWidth = plotWidth / positions.length;
  const barWidth = Math.max(4, barSlotWidth * 0.72);
  const x = (year: number) => padding.left + (year + 0.5) * barSlotWidth;
  const y = (value: number) =>
    padding.top + ((maxValue - value) / range) * plotHeight;
  const zeroY = y(0);
  const paybackYear = scenario.metrics.sustainedPaybackYears;
  const paybackX = paybackYear === null ? null : x(paybackYear);
  const maxYear = positions.length - 1;
  const yearTicks = [0, 5, 10, 15, 20, 25, 30].filter(
    (year) => year <= maxYear,
  );
  const valueTicks = [maxValue, 0, minValue].filter(
    (value, index, values) => values.indexOf(value) === index,
  );

  return (
    <div className="payback-card">
      <div className="payback-heading">
        <div>
          <span className="panel-tag">Kumulierte Geldposition</span>
          <h3>Die Amortisation wird sichtbar</h3>
        </div>
        <div className="payback-result">
          <span>nachhaltig amortisiert</span>
          <strong>{payback(scenario)}</strong>
        </div>
      </div>
      <p className="payback-copy">
        Jeder Balken zeigt die kumulierte Geldposition am Ende des Jahres. Die
        Markierung zeigt den Zeitpunkt, ab dem der Plan dauerhaft im Plus
        bleibt.
      </p>
      <div className="payback-chart-wrap">
        <svg
          viewBox={`0 0 ${width} ${height}`}
          role="img"
          aria-label={`Balkendiagramm des Amortisationsverlaufs über ${maxYear} Jahre. ${paybackYear === null ? "Der Plan amortisiert sich im Analysezeitraum nicht dauerhaft." : `Nachhaltige Amortisation nach ${paybackYear} Jahren.`}`}
        >
          {yearTicks.map((year) => (
            <line
              key={`year-grid-${year}`}
              x1={x(year)}
              x2={x(year)}
              y1={padding.top}
              y2={height - padding.bottom}
              stroke="#dce3d9"
              strokeWidth="1"
            />
          ))}
          {valueTicks.map((value) => (
            <g key={`value-grid-${value}`}>
              <line
                x1={padding.left}
                x2={width - padding.right}
                y1={y(value)}
                y2={y(value)}
                stroke={value === 0 ? "#8ca59a" : "#e5e8df"}
                strokeWidth={value === 0 ? "1.5" : "1"}
                strokeDasharray={value === 0 ? "5 4" : undefined}
              />
              <text
                x={padding.left - 10}
                y={y(value) + 4}
                textAnchor="end"
                className="chart-axis-label"
              >
                {euro(value)}
              </text>
            </g>
          ))}
          {positions.map((value, year) => {
            const valueY = y(value);
            return (
              <rect
                key={`year-bar-${year}`}
                x={x(year) - barWidth / 2}
                y={Math.min(valueY, zeroY)}
                width={barWidth}
                height={Math.max(1, Math.abs(zeroY - valueY))}
                rx="2"
                fill={value >= 0 ? "#739c6e" : "#c46f5d"}
              >
                <title>
                  {year === 0 ? "heute" : `Jahr ${year}`}: {euro(value)}
                </title>
              </rect>
            );
          })}
          {paybackX !== null && (
            <g>
              <line
                x1={paybackX}
                x2={paybackX}
                y1={padding.top}
                y2={height - padding.bottom}
                stroke="#739c6e"
                strokeWidth="1.5"
                strokeDasharray="4 4"
              />
              <circle cx={paybackX} cy={zeroY} r="6" fill="#739c6e" />
              <text
                x={Math.min(paybackX + 9, width - 132)}
                y={padding.top + 12}
                className="chart-payback-label"
              >
                Amortisation · {paybackYear} J.
              </text>
            </g>
          )}
          {yearTicks.map((year) => (
            <text
              key={`year-label-${year}`}
              x={x(year)}
              y={height - 15}
              textAnchor="middle"
              className="chart-axis-label"
            >
              {year === 0 ? "heute" : `Jahr ${year}`}
            </text>
          ))}
        </svg>
      </div>
      <div className="payback-legend">
        <span>
          <i className="legend-bar negative" /> unter 0
        </span>
        <span>
          <i className="legend-bar positive" /> über 0
        </span>
        <span>
          <i className="legend-zero" /> Ausgangspunkt
        </span>
        {scenario.annualFlows.some((flow) => flow.replacementCostEur > 0) && (
          <span className="legend-note">
            Ersatzkosten im Lebenszyklus berücksichtigt
          </span>
        )}
      </div>
    </div>
  );
}

function Warnings({ evaluation }: { evaluation: PlanEvaluationV2 | null }) {
  if (!evaluation || evaluation.warnings.length === 0) return null;
  return (
    <div className="warning-list">
      {evaluation.warnings.map((warning) => (
        <div className="warning-item" key={warning}>
          <span>↳</span>
          <p>{warning}</p>
        </div>
      ))}
    </div>
  );
}

function AssumptionsDetail({
  evaluation,
}: {
  evaluation: PlanEvaluationV2 | null;
}) {
  if (!evaluation)
    return (
      <p className="muted">
        Annahmen erscheinen nach dem ersten Berechnungsergebnis.
      </p>
    );
  const electricityPrice =
    evaluation.resolvedInput.household.electricityPriceEurPerKwh ??
    DEFAULT_ELECTRICITY_PRICE_EUR_PER_KWH;
  const householdSize = evaluation.resolvedInput.household.householdSize;
  const assumptions = [
    [
      "Strompreis",
      `${electricityPrice.toFixed(2)} €/kWh`,
      householdSize
        ? "editierbare Eingabe · Haushaltsvoreinstellung"
        : "editierbare Eingabe",
    ],
    [
      "Jährlicher Stromverbrauch",
      `${evaluation.resolvedInput.household.annualElectricityKwh.toLocaleString("de-DE")} kWh/Jahr`,
      householdSize
        ? `${householdSize}-Personen-Voreinstellung`
        : "eigene Eingabe",
    ],
    [
      "Lastprofil",
      "Vereinfachtes H0-Stundenprofil",
      "Forschung · BDEW-Haushaltsgrundlage",
    ],
    ["Einspeisevergütung", "0,075 €/kWh", "Politik · gültig ab 01.01.2026"],
    ["Realer Diskontsatz", "3 % / Jahr", "Forschung / Modelleingabe"],
    ["PV-Leistungsverlust", "0,5 % / Jahr", "Forschung / Modelleingabe"],
    [
      "Batterieersatz",
      "Jahr 13 · 50 % der ursprünglichen Kosten",
      "Forschungsstandard · editierbar",
    ],
    [
      "PV-Standardpreis",
      euro(evaluation.suggestions.pv.standardCostPerKwpEur) +
        " / kWp Material + " +
        euro(evaluation.suggestions.pv.standardRooftopInstallationEur) +
        " Dachinstallation",
      "Forschung · Material und Installation separat editierbar",
    ],
    [
      "Speicher-Standardpreis",
      euro(evaluation.suggestions.battery.standardCostPerUsableKwhEur) +
        " / nutzbare kWh Materialwert + 0 € Standardinstallation",
      "Forschung · Installation separat editierbar",
    ],
    [
      "Solarertrag",
      `${evaluation.suggestions.pv.annualYieldKwhPerKwp} kWh/kWp/Jahr`,
      `${originLabel(evaluation.suggestions.pv.source.origin)} · ${sourceStatusLabel(evaluation.externalSources.find((source) => source.provider === "PVGIS 5.3")?.status ?? "provided")}`,
    ],
  ];
  return (
    <div id="assumptions-detail" className="assumptions-detail">
      <div className="assumption-table">
        {assumptions.map(([name, value, origin]) => (
          <div className="assumption-row" key={name}>
            <span>{name}</span>
            <strong>{value}</strong>
            <small>{origin}</small>
          </div>
        ))}
      </div>
      <details className="method-details">
        <summary>Formelspur und Jahresverläufe anzeigen</summary>
        <ul>
          {evaluation.formulaTrace.map((formula) => (
            <li key={formula}>{formula}</li>
          ))}
        </ul>
        <AnnualFlowTable scenario={evaluation.selected} />
      </details>
    </div>
  );
}

function AnnualFlowTable({ scenario }: { scenario: Scenario }) {
  const flows =
    scenario.annualFlows.length > 7
      ? [...scenario.annualFlows.slice(0, 3), ...scenario.annualFlows.slice(-2)]
      : scenario.annualFlows;
  return (
    <div className="flow-table-wrap">
      <table>
        <caption>{scenario.label} · beispielhafte Jahresverläufe</caption>
        <thead>
          <tr>
            <th>Jahr</th>
            <th>Erzeugung</th>
            <th>Netzbezug</th>
            <th>Einspeisung</th>
            <th>Verpasster Eigenverbrauchswert</th>
            <th>Geldvorteil</th>
          </tr>
        </thead>
        <tbody>
          {flows.map((flow) => (
            <tr key={flow.year}>
              <td>{flow.year}</td>
              <td>{flow.generationKwh} kWh</td>
              <td>{flow.gridImportKwh} kWh</td>
              <td>{flow.exportKwh} kWh</td>
              <td>{euro(flow.missedSelfConsumptionValueEur)}</td>
              <td>
                {flow.cashBenefitEur === null ? "—" : euro(flow.cashBenefitEur)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CostEstimate({
  draft,
  evaluation,
}: {
  draft: PlanDraftV2;
  evaluation: PlanEvaluationV2 | null;
}) {
  const solarAssets = draft.assets.filter(
    (asset): asset is SolarAsset => asset.kind === "solar",
  );
  const solar = solarAssets[0];
  const battery = findAssetByKind(draft.assets, "battery");
  const selected = evaluation?.selected;
  const breakdown = selected?.metrics.upfrontCostBreakdown;
  const solarBreakdown = breakdown
    ? costBreakdownForAssetIds(
        breakdown.byAssetId,
        draft.assets
          .filter((asset): asset is SolarAsset => asset.kind === "solar")
          .map((asset) => asset.id),
      )
    : null;
  const batteryBreakdown = breakdown
    ? costBreakdownForAssetIds(
        breakdown.byAssetId,
        draft.assets
          .filter((asset): asset is BatteryAsset => asset.kind === "battery")
          .map((asset) => asset.id),
      )
    : null;
  const total = breakdown?.totals.totalEur ?? null;
  const installation = breakdown?.totals.installationEur ?? null;
  const hasUnallocatedInstallationCost = draft.assets.some(
    (asset) =>
      asset.state === "new" &&
      asset.costOverride?.totalEur !== undefined &&
      asset.costOverride.installationEur === undefined,
  );

  return (
    <aside className="cost-estimate" aria-labelledby="cost-estimate-heading">
      <div className="cost-estimate-heading">
        <div>
          <h3 id="cost-estimate-heading">Kostenübersicht</h3>
        </div>
        <a
          href={
            battery && !solar
              ? BATTERY_COSTS_ARTICLE_PATH
              : SOLAR_COSTS_ARTICLE_PATH
          }
        >
          Wie entstehen diese Werte? ↗
        </a>
      </div>
      <div className="cost-rows">
        <CostEstimateRow
          label="Solaranlage"
          detail={
            solarAssets.length === 0
              ? "nicht ausgewählt"
              : solarAssets
                  .map(
                    (asset) =>
                      `${asset.capacityKwp} kWp ${
                        asset.variant === "balcony" ? "Balkon" : "Dach"
                      }`,
                  )
                  .join(" + ")
          }
          value={
            solarAssets.length === 0
              ? "—"
              : solarBreakdown
                ? euro(nonInstallationCost(solarBreakdown))
                : "…"
          }
        />
        <CostEstimateRow
          label="Batteriespeicher"
          detail={
            battery
              ? `${battery.usableCapacityKwh} kWh nutzbar`
              : "nicht ausgewählt"
          }
          value={
            !battery
              ? "—"
              : batteryBreakdown
                ? euro(nonInstallationCost(batteryBreakdown))
                : "…"
          }
        />
        <CostEstimateRow
          label="Installation"
          detail={
            hasUnallocatedInstallationCost
              ? "Eigener Gesamtpreis · Installation nicht aufgeteilt"
              : solarAssets.length === 0
                ? "Einmalige Montage- und Anschlusskosten"
                : solarAssets.some((asset) => asset.variant === "rooftop") &&
                    solarAssets.some((asset) => asset.variant === "balcony")
                  ? "Dachmontage + Steckersolar"
                  : solar?.variant === "balcony"
                  ? "Steckersolar · standardmäßig 0 € Arbeit"
                  : "Einmalige Montage- und Anschlusskosten"
          }
          value={
            hasUnallocatedInstallationCost
              ? "nicht aufgeteilt"
              : installation === null
                ? "…"
                : euro(installation)
          }
          emphasized
        />
      </div>
      <div className="cost-total">
        <span>Anfangsinvestition</span>
        <strong>{total === null ? "…" : euro(total)}</strong>
      </div>
      <div className="cost-footnote">
        <span>
          <a href={SOLAR_COSTS_ARTICLE_PATH}>PV-Kosten</a>
          {" · "}
          <a href={BATTERY_COSTS_ARTICLE_PATH}>Speicherkosten</a>
        </span>
      </div>
    </aside>
  );
}

function CostEstimateRow({
  label,
  detail,
  value,
  emphasized = false,
}: {
  label: string;
  detail: string;
  value: string;
  emphasized?: boolean;
}) {
  return (
    <div className={`cost-row ${emphasized ? "emphasized" : ""}`}>
      <div>
        <strong>{label}</strong>
        <small>{detail}</small>
      </div>
      <span>{value}</span>
    </div>
  );
}

function costBreakdownForAssetIds(
  byAssetId: Record<string, CostBreakdown>,
  assetIds: string[],
): CostBreakdown {
  return assetIds.reduce(
    (total, assetId) => {
      const breakdown = byAssetId[assetId];
      if (!breakdown) return total;
      return {
        equipmentEur: total.equipmentEur + breakdown.equipmentEur,
        installationEur: total.installationEur + breakdown.installationEur,
        otherOneTimeEur: total.otherOneTimeEur + breakdown.otherOneTimeEur,
        totalEur: total.totalEur + breakdown.totalEur,
      };
    },
    { equipmentEur: 0, installationEur: 0, otherOneTimeEur: 0, totalEur: 0 },
  );
}

function nonInstallationCost(breakdown: CostBreakdown) {
  return breakdown.equipmentEur + breakdown.otherOneTimeEur;
}

function SolarControl({
  solar,
  evaluation,
  patchDraft,
  onRemove,
}: {
  solar: SolarAsset;
  evaluation: PlanEvaluationV2 | null;
  patchDraft: (patch: (draft: PlanDraftV2) => PlanDraftV2) => void;
  onRemove: () => void;
}) {
  const acquisition = solar.state === "new" ? "buy" : "owned";
  const suggestions = evaluation?.suggestions.pv;
  const isBalcony = solar.variant === "balcony";
  const standardCostPerKwp =
    suggestions?.standardCostPerKwpEur ??
    CURRENT_PLANNING_DEFAULTS.pvCostPerKwpEur;
  const standardRooftopInstallation =
    suggestions?.standardRooftopInstallationEur ??
    CURRENT_PLANNING_DEFAULTS.pvRooftopInstallationEur;
  const standardSolarMaterialEur = solar.capacityKwp * standardCostPerKwp;
  const standardSolarInstallationEur = isBalcony
    ? 0
    : standardRooftopInstallation;
  const hasLegacySolarTotalOverride =
    solar.costOverride?.totalEur !== undefined &&
    solar.costOverride.equipmentEur === undefined &&
    solar.costOverride.installationEur === undefined;
  const solarMaterialEur =
    solar.costOverride?.equipmentEur ?? standardSolarMaterialEur;
  const solarInstallationEur =
    solar.costOverride?.installationEur ?? standardSolarInstallationEur;
  const standardSolarTotalEur =
    standardSolarMaterialEur + standardSolarInstallationEur;
  const solarTotalEur =
    solar.costOverride?.totalEur ??
    solarMaterialEur +
      solarInstallationEur +
      (solar.costOverride?.otherOneTimeEur ?? 0);
  const solarPriceHint =
    "Standard: " +
    euro(standardSolarMaterialEur) +
    " Material + " +
    euro(standardSolarInstallationEur) +
    " Installation" +
    " = " +
    euro(standardSolarTotalEur) +
    " Gesamt. " +
    (hasLegacySolarTotalOverride
      ? "Bisheriger Gesamtpreis ist noch nicht aufgeteilt. "
      : "Eigene Werte ersetzen jeweils nur diese Position. ");
  const solarYieldHint = isBalcony
    ? "Balkon-Standard: 0,8 kWp Modulleistung / 0,8 kVA Wechselrichter. Die gesetzlichen Steckersolar-Grenzen und eine mögliche Wechselrichterbegrenzung sind separat zu prüfen."
    : "Die vorgeschlagene Größe basiert auf " +
      (suggestions?.annualYieldKwhPerKwp ?? "…") +
      " kWh/kWp/Jahr.";
  const solarPresets: ReadonlyArray<readonly [string, number | undefined]> =
    isBalcony
      ? [
          ["klein", 0.4],
          ["standard", CURRENT_PLANNING_DEFAULTS.balconyPvCapacityKwp],
        ]
      : [
          ["klein", suggestions?.smallKwp],
          ["mittel", suggestions?.recommendedKwp],
          ["groß", suggestions?.largeKwp],
        ];
  const update = (value: Partial<SolarAsset>) =>
    patchDraft((current) => ({
      ...current,
      assets: current.assets.map((asset) =>
        asset.id === solar.id ? { ...asset, ...value } : asset,
      ) as EnergyAsset[],
    }));
  const updateCost = (
    field: "equipmentEur" | "installationEur",
    value: number | undefined,
  ) =>
    patchDraft((current) => ({
      ...current,
      assets: current.assets.map((asset) =>
        asset.id === solar.id
          ? {
              ...asset,
              costOverride: explicitCostOverride(
                asset.costOverride,
                field,
                value,
              ),
            }
          : asset,
      ) as EnergyAsset[],
    }));
  return (
    <ComponentPanel
      title={
        solar.variant === "balcony"
          ? "Balkon-Solaranlage"
          : "Solaranlage auf dem Dach"
      }
      tag="eigene Erzeugung"
      tone="sun"
      onRemove={onRemove}
    >
      <AcquisitionToggle
        label="Solaranlage"
        value={acquisition}
        onChange={(value) =>
          update({ state: value === "buy" ? "new" : "existing" })
        }
      />
      <div
        className="preset-row"
        role="group"
        aria-label="Voreinstellungen für die Solaranlagengröße"
      >
        {solarPresets.map(([name, value]) => (
          <button
            type="button"
            className={
              Math.abs(solar.capacityKwp - (value ?? -1)) < 0.01
                ? "preset active"
                : "preset"
            }
            key={name}
            onClick={() => value && update({ capacityKwp: value })}
          >
            {name}
            <strong>{value ?? "—"} kWp</strong>
          </button>
        ))}
      </div>
      <div className="control-grid">
        <Field label="Exakte Größe" hint="Editierbare Planungseingabe.">
          <div className="input-with-unit">
            <input
              aria-label="Exakte Größe der Solaranlage"
              type="number"
              min={isBalcony ? 0.4 : 2}
              max={isBalcony ? 2 : 20}
              step={isBalcony ? 0.1 : 0.5}
              value={solar.capacityKwp}
              onChange={(event) =>
                update({
                  capacityKwp: numeric(event.target.value, solar.capacityKwp),
                })
              }
            />
            <span>kWp</span>
          </div>
        </Field>
        <Field
          label="Materialpreis"
          hint={
            acquisition === "owned"
              ? "Bereits vorhandene Anlage wird nicht als neue Investition gerechnet."
              : solarPriceHint
          }
        >
          <div className="input-with-unit">
            <input
              aria-label="Materialpreis der Solaranlage"
              type="number"
              min="0"
              step="100"
              placeholder={standardSolarMaterialEur.toLocaleString("de-DE")}
              disabled={acquisition === "owned"}
              value={solar.costOverride?.equipmentEur ?? ""}
              onChange={(event) =>
                updateCost(
                  "equipmentEur",
                  event.target.value
                    ? numeric(event.target.value, 0)
                    : undefined,
                )
              }
            />
            <span>€</span>
          </div>
        </Field>
        <Field
          label="Installationskosten"
          hint={
            acquisition === "owned"
              ? "Bereits vorhandene Anlage wird nicht als neue Investition gerechnet."
              : isBalcony
                ? "0 € eintragen, wenn auch Montage und Anschluss selbst übernommen werden."
                : "Nur der Installationsanteil des Angebots; Material wird separat erfasst."
          }
        >
          <div className="input-with-unit">
            <input
              aria-label="Installationskosten der Solaranlage"
              type="number"
              min="0"
              step="100"
              placeholder={standardSolarInstallationEur.toLocaleString("de-DE")}
              disabled={acquisition === "owned"}
              value={solar.costOverride?.installationEur ?? ""}
              onChange={(event) =>
                updateCost(
                  "installationEur",
                  event.target.value
                    ? numeric(event.target.value, 0)
                    : undefined,
                )
              }
            />
            <span>€</span>
          </div>
        </Field>
      </div>
      <div className="cost-total">
        <span>Gesamtpreis</span>
        <strong>{euro(solarTotalEur)}</strong>
      </div>
      <p className="control-source">
        {solarYieldHint} Ertragsquelle:{" "}
        {suggestions ? originLabel(suggestions.source.origin) : "Berechnung"}.
        Sie ersetzt keine statische Dachprüfung.{" "}
        {isBalcony
          ? "Steckersolar ist als Plug-in-Standard mit 0 € Installationsarbeit angesetzt; Halterung, Sondermontage oder Elektrikerarbeiten können extra kosten."
          : "Die Dachinstallation ist ein Planungswert und kein verbindliches Angebot."}
      </p>
    </ComponentPanel>
  );
}

function BatteryControl({
  draft,
  evaluation,
  patchDraft,
  onRemove,
}: {
  draft: PlanDraftV2;
  evaluation: PlanEvaluationV2 | null;
  patchDraft: (patch: (draft: PlanDraftV2) => PlanDraftV2) => void;
  onRemove: () => void;
}) {
  const battery = findAssetByKind(draft.assets, "battery");
  if (!battery) return null;
  const acquisition = battery.state === "new" ? "buy" : "owned";
  const replacement = battery.replacement ?? { year: 13, costFraction: 0.5 };
  const standardCostPerUsableKwh =
    evaluation?.suggestions.battery.standardCostPerUsableKwhEur ??
    CURRENT_PLANNING_DEFAULTS.batteryCostPerUsableKwhEur;
  const standardBatteryTotalEur =
    battery.usableCapacityKwh * standardCostPerUsableKwh;
  const standardBatteryMaterialEur = standardBatteryTotalEur;
  const standardBatteryInstallationEur = 0;
  const hasLegacyBatteryTotalOverride =
    battery.costOverride?.totalEur !== undefined &&
    battery.costOverride.equipmentEur === undefined &&
    battery.costOverride.installationEur === undefined;
  const batteryMaterialEur =
    battery.costOverride?.equipmentEur ?? standardBatteryMaterialEur;
  const batteryInstallationEur =
    battery.costOverride?.installationEur ?? standardBatteryInstallationEur;
  const batteryTotalEur =
    battery.costOverride?.totalEur ??
    batteryMaterialEur +
      batteryInstallationEur +
      (battery.costOverride?.otherOneTimeEur ?? 0);
  const batteryPriceHint =
    "Standard: " +
    euro(standardBatteryMaterialEur) +
    " Material + " +
    euro(standardBatteryInstallationEur) +
    " Installation = " +
    euro(standardBatteryTotalEur) +
    " Gesamt. " +
    (hasLegacyBatteryTotalOverride
      ? "Bisheriger Gesamtpreis ist noch nicht aufgeteilt."
      : "Eigene Werte ersetzen jeweils nur diese Position.");
  const update = (value: Partial<BatteryAsset>) =>
    patchDraft((current) => ({
      ...current,
      assets: current.assets.map((asset) =>
        asset.id === battery.id ? { ...asset, ...value } : asset,
      ) as EnergyAsset[],
    }));
  const updateCost = (
    field: "equipmentEur" | "installationEur",
    value: number | undefined,
  ) =>
    patchDraft((current) => ({
      ...current,
      assets: current.assets.map((asset) =>
        asset.id === battery.id
          ? {
              ...asset,
              costOverride: explicitCostOverride(
                asset.costOverride,
                field,
                value,
              ),
            }
          : asset,
      ) as EnergyAsset[],
    }));
  return (
    <ComponentPanel
      title="Batteriespeicher"
      tag="Solarstrom verschieben"
      tone="moss"
      onRemove={onRemove}
    >
      <AcquisitionToggle
        label="Batteriespeicher"
        value={acquisition}
        onChange={(value) =>
          update({ state: value === "buy" ? "new" : "existing" })
        }
      />
      <div className="control-grid">
        <Field
          label="Nutzbare Kapazität"
          hint={`Vorschlag: ${evaluation?.suggestions.battery.recommendedKwh ?? "…"} kWh`}
        >
          <div className="input-with-unit">
            <input
              aria-label="Nutzbare Kapazität des Batteriespeichers"
              type="number"
              min="1"
              max="50"
              step="1"
              value={battery.usableCapacityKwh}
              onChange={(event) =>
                update({
                  usableCapacityKwh: numeric(
                    event.target.value,
                    battery.usableCapacityKwh,
                  ),
                })
              }
            />
            <span>kWh</span>
          </div>
        </Field>
        <Field
          label="Materialpreis"
          hint={
            acquisition === "owned"
              ? "Bereits vorhandener Speicher wird nicht als neue Investition gerechnet."
              : batteryPriceHint
          }
        >
          <div className="input-with-unit">
            <input
              aria-label="Materialpreis des Batteriespeichers"
              type="number"
              min="0"
              step="100"
              placeholder={standardBatteryMaterialEur.toLocaleString("de-DE")}
              disabled={acquisition === "owned"}
              value={battery.costOverride?.equipmentEur ?? ""}
              onChange={(event) =>
                updateCost(
                  "equipmentEur",
                  event.target.value
                    ? numeric(event.target.value, 0)
                    : undefined,
                )
              }
            />
            <span>€</span>
          </div>
        </Field>
        <Field
          label="Installationskosten"
          hint={
            acquisition === "owned"
              ? "Bereits vorhandener Speicher wird nicht als neue Investition gerechnet."
              : "Nur der Installationsanteil des Angebots; Material wird separat erfasst."
          }
        >
          <div className="input-with-unit">
            <input
              aria-label="Installationskosten des Batteriespeichers"
              type="number"
              min="0"
              step="100"
              placeholder={standardBatteryInstallationEur.toLocaleString(
                "de-DE",
              )}
              disabled={acquisition === "owned"}
              value={battery.costOverride?.installationEur ?? ""}
              onChange={(event) =>
                updateCost(
                  "installationEur",
                  event.target.value
                    ? numeric(event.target.value, 0)
                    : undefined,
                )
              }
            />
            <span>€</span>
          </div>
        </Field>
        <Field
          label="Ersatzjahr"
          hint="Standardmäßig ist ein Lebenszyklusereignis enthalten."
        >
          <div className="input-with-unit">
            <input
              aria-label="Ersatzjahr des Batteriespeichers"
              type="number"
              min="2"
              max="25"
              step="1"
              value={replacement.year}
              onChange={(event) =>
                update({
                  replacement: {
                    ...replacement,
                    year: numeric(event.target.value, 13),
                  },
                })
              }
            />
            <span>Jahr</span>
          </div>
        </Field>
        <Field
          label="Ersatzkosten"
          hint="Anteil am ursprünglichen Speicherpreis."
        >
          <div className="input-with-unit">
            <input
              aria-label="Anteil der Ersatzkosten am ursprünglichen Speicherpreis"
              type="number"
              min="0"
              max="2"
              step="0.05"
              value={replacement.costFraction}
              onChange={(event) =>
                update({
                  replacement: {
                    ...replacement,
                    costFraction: numeric(event.target.value, 0.5),
                  },
                })
              }
            />
            <span>× Originalpreis</span>
          </div>
        </Field>
      </div>
      <div className="cost-total">
        <span>Gesamtpreis</span>
        <strong>{euro(batteryTotalEur)}</strong>
      </div>
    </ComponentPanel>
  );
}

function AcquisitionToggle({
  label,
  value,
  onChange,
}: {
  label: string;
  value: Acquisition;
  onChange: (value: Acquisition) => void;
}) {
  return (
    <div className="acquisition-control">
      <span className="field-label">{label}: Anschaffung</span>
      <div
        className="acquisition-toggle"
        role="group"
        aria-label={`${label} kaufen oder bereits vorhanden`}
      >
        <button
          type="button"
          className={value === "buy" ? "active" : ""}
          aria-pressed={value === "buy"}
          onClick={() => onChange("buy")}
        >
          kaufen
        </button>
        <button
          type="button"
          className={value === "owned" ? "active" : ""}
          aria-pressed={value === "owned"}
          onClick={() => onChange("owned")}
        >
          bereits vorhanden
        </button>
      </div>
      {value === "owned" && (
        <small>
          Die Anschaffungskosten fließen nicht in die Amortisation ein.
        </small>
      )}
    </div>
  );
}

function ComponentPanel({
  title,
  tag,
  tone,
  onRemove,
  children,
}: {
  title: string;
  tag: string;
  tone: string;
  onRemove: () => void;
  children: ReactNode;
}) {
  return (
    <article className={`component-panel tone-${tone}`}>
      <div className="panel-heading">
        <div>
          <span className="panel-tag">{tag}</span>
          <h3>{title}</h3>
        </div>
        <button type="button" className="remove-button" onClick={onRemove}>
          entfernen <span aria-hidden="true">×</span>
        </button>
      </div>
      {children}
    </article>
  );
}

function ComponentButton({
  icon,
  title,
  subtitle,
  active,
  onClick,
}: {
  icon: string;
  title: string;
  subtitle: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`component-button ${active ? "active" : ""}`}
      onClick={onClick}
      aria-pressed={active}
    >
      <span className="component-icon">{icon}</span>
      <span className="component-copy">
        <strong>{title}</strong>
        <small>{subtitle}</small>
      </span>
      <span className="component-toggle">{active ? "−" : "+"}</span>
    </button>
  );
}

function SectionHeading({
  number,
  title,
  id,
}: {
  number: string;
  title: string;
  id: string;
}) {
  return (
    <div className="section-heading">
      <span className="section-number">{number}</span>
      <h2 id={id}>{title}</h2>
    </div>
  );
}
function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span className="field-label">{label}</span>
      {children}
      {hint && <small>{hint}</small>}
    </label>
  );
}
function numeric(value: string, fallback: number) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}
function explicitCostOverride(
  current: AssetCostOverride | undefined,
  field: "equipmentEur" | "installationEur",
  value: number | undefined,
) {
  const next: AssetCostOverride = { ...(current ?? {}) };
  delete next.totalEur;
  if (value === undefined) delete next[field];
  else next[field] = value;
  return Object.keys(next).length > 0 ? next : undefined;
}
function euro(value: number) {
  return new Intl.NumberFormat("de-DE", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}
function originLabel(origin: string) {
  switch (origin) {
    case "user":
      return "Nutzereingabe";
    case "external":
      return "externe Quelle";
    case "policy":
      return "politische Vorgabe";
    case "research":
      return "Forschung";
    case "model-default":
      return "Modellstandard";
    default:
      return origin;
  }
}
function sourceStatusLabel(status: string) {
  switch (status) {
    case "live":
      return "aktuell";
    case "fallback":
      return "Ersatzwert";
    case "provided":
      return "bereitgestellt";
    default:
      return status;
  }
}
function verdictLabel(verdict: string) {
  switch (verdict) {
    case "helps":
      return "positiv";
    case "hurts":
      return "negativ";
    case "neutral":
      return "neutral";
    case "unavailable":
      return "nicht verfügbar";
    default:
      return verdict;
  }
}
function payback(scenario: Scenario) {
  return scenario.metrics.sustainedPaybackYears === null
    ? scenario.unavailableReason
      ? "Nicht verfügbar"
      : `nicht innerhalb von ${scenario.annualFlows.length} Jahren`
    : `${scenario.metrics.sustainedPaybackYears} Jahre`;
}
